import React from "react";
import { BdWallet2 } from "solar-icon-react/bd";
import { IDefaultPackage, IPackages } from "@/interfaces";
import { cn, formatNumber } from "@/lib";
import styles from "@/components/Payment/paymentCheckout.module.css";

interface IRadioProps {
  packages: IPackages[];
  defaultValue: IDefaultPackage;
  selectedPackage: IDefaultPackage | IPackages | null;
  setSelectedPackage: (pkg: IPackages | IDefaultPackage) => void;
}

const packageThemes = [
  { plan: "#54b8f1", background: "#f2faff" },
  { plan: "#4ccf8a", background: "#f3fff7" },
  { plan: "#faa763", background: "#fff9f1" },
  { plan: "#ff859c", background: "#fff5f7" },
];

const RadioPackageGroup: React.FC<IRadioProps> = ({
  packages,
  defaultValue,
  selectedPackage,
  setSelectedPackage,
}) => {
  const selectedDefault = selectedPackage?.id === "p0";
  const highestDiscount = Math.max(
    0,
    ...packages.map((pkg) => Number(pkg.discount?.match(/\d+/)?.[0] || 0)),
  );

  return (
    <form onSubmit={(event) => event.preventDefault()} className="w-full">
      <div role="radiogroup" aria-label="انتخاب بسته‌های استعلام">
        <div className={styles.sectionTop}>
          <h3>نوع سفارش خود را انتخاب کنید</h3>
          <span className={styles.miniTag}>انتخاب سفارش</span>
        </div>
        <label className="block cursor-pointer" htmlFor="p0">
          <input
            id="p0"
            className="sr-only"
            type="radio"
            name="package-selection"
            checked={selectedDefault}
            onChange={() => setSelectedPackage(defaultValue)}
          />
          <span
            className={cn(
              styles.singleOption,
              selectedDefault && styles.selected,
            )}
          >
            <span className={styles.selector} />
            <span className={styles.singleIcon}>
              <BdWallet2 size={24} />
            </span>
            <span className={styles.optionMain}>
              <strong>۱ {defaultValue.name}</strong>
              <span>پرداخت برای همین استعلام، بدون خرید بسته</span>
            </span>
            <span className={styles.optionPrice}>
              {defaultValue.price}
              <small className={styles.currency}>تومان</small>
            </span>
          </span>
        </label>

        <div className={styles.packageHeading}>
          <div>
            <h3>بسته‌های {defaultValue.name}</h3>
            <p>تخفیف بیشتر برای استعلام‌های همین سرویس</p>
          </div>
          {highestDiscount > 0 && (
            <span className={styles.savings}>تا {highestDiscount}٪ تخفیف</span>
          )}
        </div>
        <div className={styles.packageGrid}>
          {packages.map((pkg, index) => {
            const selected = selectedPackage?.id === pkg.id;
            const theme = packageThemes[index % packageThemes.length];
            const packageStyle: React.CSSProperties &
              Record<"--plan" | "--plan-bg", string> = {
              "--plan": theme.plan,
              "--plan-bg": theme.background,
            };

            return (
              <label
                key={pkg.id}
                className="block cursor-pointer"
                htmlFor={`package-${pkg.id}`}
              >
                <input
                  id={`package-${pkg.id}`}
                  className="sr-only"
                  type="radio"
                  name="package-selection"
                  checked={selected}
                  onChange={() => setSelectedPackage(pkg)}
                />
                <span
                  className={cn(
                    styles.package,
                    selected && styles.packageSelected,
                  )}
                  style={packageStyle}
                >
                  <span className={styles.packageTop}>
                    <span className={styles.packageTitle}>
                      <strong>{pkg.title}</strong>
                      <small>{pkg.services[0] || defaultValue.name}</small>
                    </span>
                    {pkg.discount && (
                      <span className={styles.discount}>{pkg.discount}</span>
                    )}
                  </span>
                  <span className={styles.packageMeta}>
                    اعتبار: {pkg.duration}
                  </span>
                  <span className={styles.packageBottom}>
                    <strong className={styles.packageCost}>
                      {formatNumber(Number(pkg.price))} تومان
                    </strong>
                    {pkg.perInquiryPrice && (
                      <span className={styles.packageUnit}>
                        هر استعلام {formatNumber(Number(pkg.perInquiryPrice))}
                      </span>
                    )}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <div className={styles.featureStrip}>
          <div>
            تمام بسته‌ها فقط برای <b>{defaultValue.name}</b> هستند و برای سایر
            خدمات قابل استفاده نیستند.
          </div>
        </div>
      </div>
    </form>
  );
};

export default RadioPackageGroup;
